import { Component, Output, EventEmitter, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionLike, take } from 'rxjs';

import { User                                          } from '@shared/factories';
import { PillModel, SidebarRouteExtended, TogglerModel } from '@shared/models';
import { NotificationService, PillsService, SessionStorageService, UserService } from '@shared/services';

@Component({
  selector:      'basic-pills',
  templateUrl: './basic-pills.component.html',
  styleUrls:  ['./basic-pills.component.sass'],
  host: { class: 'tablet-hide' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BasicPillsComponent implements OnInit, OnDestroy {

  subscriptions: SubscriptionLike[] = [];

  user:          User;
  title:         string;
  activeTab:     SidebarRouteExtended;
  activeToggler: TogglerModel;

  entries:       PillModel[];
  singleSelect:  boolean;

  get canShowMore(): boolean {
    return this.entries && (this.entries.length > 4 || this.entries.some(e => !e.favourite));
  }

  @Output() callback = new EventEmitter<any>();
  constructor(
    private userService:           UserService,
    private sessionStorageService: SessionStorageService,
    private pillsService:          PillsService,
    private notificationService:   NotificationService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(this.userService.currentUser.subscribe(user => {
      this.user = user;

      if (!this.activeTab) this.loadActiveTab();
    }));
    //TODO: refactor try to improve this
    this.subscriptions.push(this.pillsService.reload.subscribe(_ => {
      this.notificationService.wait();
      this.callback.emit();
    }))
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  private loadActiveTab(): void {
    this.sessionStorageService.activeTab.pipe(take(1)).subscribe(tab => {
      this.activeTab = tab;
      this.loadTogglers();
    });
  }

  private loadTogglers(): void {
    this.subscriptions.push(this.sessionStorageService.headerTogglers.subscribe(toggler => {
      if      (this.activeTab.identifier === 'archive')    this.activeToggler = toggler.archive.find(f => f.active);
      else if (this.activeTab.identifier === 'failed-erp') this.activeToggler = toggler.failed_erp.find(f => f.active);
      else this.activeToggler = null;

      if (!this.entries) this.loadPills();
      else this.mapBetaLocations();
    }));
  }

  private loadPills(): void {
    this.subscriptions.push(this.pillsService.pills.subscribe(pills => {
      this.entries      = pills;
      this.title        = this.pillsService.title;
      this.singleSelect = this.pillsService.singleSelect;
      if (!this.entries) this.renderEntries(pills);
      else this.entries = pills;
    }));
  }

  getMainMenuPills(): PillModel[] {
    return this.entries.filter(e => e.favourite);
  }

  private renderEntries(pills: PillModel[]): void {
    if (pills.length <= 4) pills = this.showAllEntries(pills);

    let shown = pills.filter(e => e.favourite);
    if (shown.length === 0) pills = this.showFirstFourEntry(pills);

    this.entries = pills;
    this.sessionStorageService.updatePills(this.entries);

    this.mapBetaLocations();
  }

  private mapBetaLocations(): void {
    let betaFlag    = this.activeToggler?.beta || this.activeTab.betaFlag;
    let betaFeature = this.user?.features && this.user.features[betaFlag];

    let betaFullyEnabled = betaFeature && this.user.features[betaFlag] === true;
    let betaLocations    = betaFeature && this.user.features[betaFlag] || [];

    if (betaFlag && !betaFullyEnabled) this.entries.forEach(e => { e.disabled = !betaLocations.find(l => +e.location === +l); });
    else this.entries.forEach(e => { e.disabled = false });
  }

  private showAllEntries(pills: PillModel[]): PillModel[] {
    return pills.map(e => {
      e.favourite = true;
      return e;
    });
  }

  private showFirstFourEntry(pills: PillModel[]): PillModel[] {
    return pills.map((e, index) => {
      if (index < 4) e.favourite = true;
      return e;
    });
  }

  togglePill(entry: PillModel): void {
    if (this.singleSelect) this.toggleSingleSelectPill(entry);
    else if (entry.active) this.disablePill(entry);
    else this.enablePill(entry);
  }

  private toggleSingleSelectPill(entry: PillModel): void {
    if (!entry.active) {
      this.entries.forEach(ent => ent.active = false);
      this.enablePill(entry);
    }
  }

  private enablePill(entry: PillModel, skipUpdate: boolean = null): void {
    if (!entry.active && this.pillsService.checkLimits(this.entries)) {
      entry.active = true;
      this.pillsService.persistPills(this.entries, skipUpdate);
    }
  }

  private disablePill(entry: PillModel): void {
    entry.active = false;
    if (this.singleSelect) {
      let active = this.getMainMenuPills().find(p => p.active);
      if (!active) this.getMainMenuPills()[0].active = true;
    }
    this.sessionStorageService.updatePills(this.entries);
    this.pillsService.persistPills(this.entries, false);
  }

  removePill(e: Event, entry: PillModel): void {
    e.preventDefault();
    e.stopPropagation();
    entry.favourite = false;
    this.disablePill(entry);
  }

  pillsActiveLength(): number {
    return this.entries.filter(e => e.active).length ? this.entries.filter(e => e.active).length : this.entries.length;
  }

  openPopup(): void {
    this.pillsService.openPicker();
  }
}
